<template>
  <div class="ce-profile" :class="{ activated }">
    <header
      :draggable="profile.order !== undefined && !editingName"
      class="pdg-"
      @dragstart="startDrag($event)"
      @dragend="stopDrag($event)"
    >
      <Btn
        v-show="profile.order !== undefined"
        ref="handle"
        variant="bare"
        class="handle"
      >
        <div draggable>
          <Icon id="drag" />
        </div>
      </Btn>
      <h3
        v-if="profile.disableEdit || profile.type === 'last_call_profile'"
        class="h4 mrg0 mrgl"
      >
        <Icon id="phone-up" />
        {{ name }}
      </h3>
      <h3
        v-else
        class="h4 mrg0 mrgl editable"
        @click="editingName = true"
        @mouseover="hoveringName = true"
        @mouseleave="hoveringName = false"
      >
        <Icon id="phone-up" />
        <span
          v-if="!editingName"
          ref="title"
          class="mrgl-"
          tabindex="0"
          @focus="hoveringName = true"
          @blur="hoveringName = false"
        >
          {{ name }}
        </span>
        <input v-else v-model="newName" v-focus type="text" />
        <span class="editable__confirm mrgl">
          <Btn variant="link" class="pdg0" @click.stop="stopEditName">
            <Icon v-if="editingName" id="check-circle" />
          </Btn>
          <Btn
            variant="link"
            class="pdg0 mrgh-"
            @click.prevent.stop="stopEditName(true)"
          >
            <Icon v-if="editingName" id="remove-circle" />
          </Btn>
        </span>
      </h3>
      <Btn v-if="!profile.special" variant="link" @click="deleteProfile">
        <Icon id="trash" />
      </Btn>
      <div class="vertical-divider mrgh" />
      <Toggle
        v-model="activated"
        :label="$t('exploitationcenter.profile.active')"
        :label-disabled="$t('exploitationcenter.profile.inactive')"
        :disabled="profile.disableEdit"
      />
    </header>
    <div v-if="!profile.special" class="pdg- ce-profile__body">
      <label @click.prevent="$refs.select.$el.querySelector('input').focus()">
        <span class="mrgh">{{ $t('exploitationcenter.lines') }}</span>
        <Select
          ref="select"
          :value="selectedCourses"
          class="v-select--taggable"
          :placeholder="$t('exploitationcenter.lines-search')"
          :options="availableCourses"
          label="label"
          autofocus
          multiple
          @input="event => deconstructCourses(event)"
        >
          <template v-slot:no-options>
            {{ $t('filters.search.no-options') }}
          </template>
        </Select>
      </label>
      <label class="mrgt">
        <span class="mrgh">{{ $t('exploitationcenter.to-call') }}</span>
        <Select
          v-model="selectedUsernames"
          :placeholder="$t('exploitationcenter.to-call-search')"
          :options="availableUsernames"
          label="label"
          autofocus
        >
          <template v-slot:no-options>
            {{ $t('filters.search.no-options') }}
          </template>
        </Select>
      </label>
    </div>
    <div v-else class="pdg- ce-profile__body--special">
      {{ profile.special }}
      <label v-if="profile.type === 'last_call_profile'" class="mrgt">
        <span class="mrgr">
          {{ $t('exploitationcenter.profile.limit-minutes') }}
        </span>
        <InputNumber v-model="limit" unit="min" :min="1" />
      </label>
    </div>
  </div>
</template>

<script>
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import InputNumber from '@/components/ui/InputNumber.vue';
import { debounce } from 'lodash';

export default {
  components: {
    Select,
    Toggle,
    InputNumber,
  },

  directives: {
    /** Directly focus on the element upon insertion */
    focus: {
      inserted(el) {
        el.focus();
      },
    },
  },

  props: {
    /** @type {import('vue').PropOptions<import('@/store/modules/routing-profiles').Profile>} the profile */
    profile: { type: Object, required: true },
    /** @type {import('vue').PropOptions<import('@/store/modules/users').User[]>} a list of users that can be selected */
    availableUsers: { type: Array, required: true },
    /** @type {import('vue').PropOptions<string[]>} a list of lines that can be selected */
    availableLines: { type: Array, required: true },
    /** @type {import('vue').PropOptions<string[]>} a list of regions that can be selected */
    availableRegions: { type: Array, required: true },
    /** @type {import('vue').PropOptions<boolean>} whether the user is currently dragging a profile */
    isDragging: { type: Boolean, required: false },
  },

  data() {
    return {
      /** @type {string} the name of the profile */
      name: this.profile.name,
      /** @type {string} the new name of the profile, used when modifying the name of the profile */
      newName: this.profile.name,
      /** @type {?string} the previous name of the profile, used when cancelling a modification of the name of the profile */
      oldName: null,
      /** @type {boolean} whether the user is editing the name of the profile */
      editingName: false,
      /** @type {boolean} whether the user is hovering the name of the profile */
      hoveringName: false,
      /** @type {string[]} a list of selected lines */
      selectedLines: this.profile.lines,
      /** @type {string[]} a list of selected regions */
      selectedRegions: this.profile.regions,
      /** @type {number} the number of minutes for last_call_profile */
      limit: this.profile.limit_minutes,
      /** @type {?Function} a debounced function to save the edition of a limit change (see the 'limit' watcher below) */
      debounceFnLimit: null,
      /** @type {string} the selected username (label of the user) */
      selectedUsernames: this.availableUsers.find(
        user => this.profile.target_user?.[0] === user.username
      )?.label,
      // ToDo: restore when API is ready
      /*selectedUsernames:
        this.availableUsers.filter(user =>
          this.profile.target_user?.includes(user.username)
        ) ?? [],*/
      /** @type {boolean} whether the profile is active */
      activated: this.profile.is_active,
    };
  },

  computed: {
    /** @returns {string[]} A list of available usernames derived from the list of available users */
    availableUsernames() {
      return this.availableUsers.map(user => user.label).sort();
    },
    /** @returns {import('@/store/modules/users').User} The currently selected user */
    selectedUsers() {
      return this.availableUsers.find(
        user => user.label === this.selectedUsernames
      );
      // ToDo: restore when API is ready
      /*
      return this.availableUsers.filter(user =>
        [this.selectedUsernames].includes(user.label)
      );
      */
    },
    /** @returns {string[]} A list of courses, derived from the lists of both lines and regions */
    availableCourses() {
      return this.availableLines.concat(this.availableRegions).sort();
    },
    /** @returns {string[]} A list of selected courses, containing both the lists of selected lines and regions */
    selectedCourses() {
      return this.selectedLines.concat(this.selectedRegions);
    },
  },

  watch: {
    name() {
      this.editProfile();
    },
    activated() {
      this.editProfile();
    },
    selectedUsernames() {
      this.editProfile();
    },
    selectedLines() {
      this.editProfile();
    },
    limit() {
      const fn = () => {
        this.editProfile();
      };
      if (!this.debounceFnLimit) {
        this.debounceFnLimit = debounce(fn, 500);
      }
      this.debounceFnLimit();
    },
    editingName() {
      this.oldName = this.name;
    },
  },

  methods: {
    /**
     * Selected courses contain both regions and lines. Use this method
     * in order to know what elements should be attributed to
     * regions, and what elements should be attributed to lines.
     * @param {string[]} courses A list of selected courses
     */
    deconstructCourses(courses) {
      const lines = this.availableLines.filter(line => courses.includes(line));
      const regions = this.availableRegions.filter(region =>
        courses.includes(region)
      );
      this.selectedLines = lines;
      this.selectedRegions = regions;
    },
    /**
     * Use this method to notifiy the parent's component that the
     * profile was edited.
     */
    editProfile() {
      /** @type {import('@/store/modules/routing-profiles').Profile} */
      let profile = {
        name: this.name,
        order: this.profile.order,
        is_active: this.activated,
      };
      if (this.profile.type === 'course_profile') {
        profile.lines = this.selectedLines;
        profile.regions = this.selectedRegions;
        profile.target_user = [this.selectedUsers].map(user => user?.username); // ToDo: remove the "array-isation" when API is ready
        profile.type = 'course_profile';
      } else if (this.profile.type === 'last_call_profile') {
        profile.limit_minutes = this.limit;
        profile.type = 'last_call_profile';
      }
      this.$emit('edit', profile);
    },
    /**
     * Use this method to notify the parent's component that it should
     * delete the profile
     */
    deleteProfile() {
      if (confirm(this.$t('exploitationcenter.profile.confirm'))) {
        this.$emit('delete');
      }
    },
    /**
     * Use this method to notify the parent's component that the
     * dragging started. Set the basic info of the dragging event.
     * @param {DragEvent} evt the startdrag event
     */
    startDrag(evt) {
      evt.dataTransfer.dropEffect = 'move';
      evt.dataTransfer.effectAllowed = 'move';
      evt.dataTransfer.setData('text/plain', this.profile.order);
      this.$emit('startDrag');
    },
    /**
     * Use this method to notify the parent's component that the
     * dragging stopped.
     */
    stopDrag() {
      this.$emit('stopDrag', this.profile.order);
    },
    /**
     * Set the new value of the profile's name after a name editing
     * @param {boolean} cancel whether the user cancelled the name editing
     */
    stopEditName(cancel = false) {
      this.editingName = false;
      if (cancel === true) {
        this.name = this.oldName;
        this.newName = this.name;
      } else {
        this.name = this.newName;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ce-profile {
  border: 2px dashed $alt-200;

  > header {
    border-bottom: 2px dashed $alt-200;
    display: flex;
    align-items: center;

    > h3 {
      flex: 1 0 auto;
    }

    > .vertical-divider {
      height: 1.5em;
      background-color: black;
      width: 1px;
    }
  }

  &.activated {
    border: 2px solid $brand-500;

    > header {
      border-bottom: 2px solid $brand-500;
      background-color: $brand-50;
    }
  }

  &__body {
    label {
      display: flex;
      align-items: center;

      span {
        flex: 1;
      }

      .v-select {
        flex: 0 0 35em;
      }
    }

    &--special {
      label {
        display: block;
      }
    }
  }
}

.slide-enter-active,
.slide-leave-active {
  opacity: 1;
  transform: translateX(0) rotate(0);
  transition: all 0.1s ease-in-out;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateX(-1rem) rotate(-5deg);
}

.editable {
  position: relative;
  border: 2px solid transparent;
  padding: 5px 4px 3px 4px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;

  > input {
    flex: 1;
  }

  &:hover {
    border: 2px solid $brand-500;
  }
}

.edit-hint {
  pointer-events: none;
  opacity: 0;

  &.visible {
    opacity: 1;
    pointer-events: all;
  }
}
</style>
